import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChair,
  faClone,
  faFloppyDisk,
  faImage,
  faMinus,
  faPlus,
  faRulerCombined,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";

import { api } from "../App";
import { reviewRoom, SERVICE_AISLE_CM } from "./tableArrangement";
import { OrangeButton } from "../components/DefaultImports";
import SwitchOnAndOff from "../components/SwitchOnAndOff";
import {
  createPlaceOfTable,
  createTables,
  fetchPlaceOfTable,
  fetchTables,
} from "../actions/restaurantActions";

const LEGACY_CANVAS_WIDTH = 750;
const LEGACY_CANVAS_HEIGHT = 400;
const DEFAULT_ROOM_WIDTH_M = 10;
const DEFAULT_ROOM_HEIGHT_M = 6;

// Smallest real-world size any table or room object may have, in centimetres.
// Thin things like walls and door frames need to go well below the old 10 cm
// floor to be drawn at their true thickness.
const MIN_ENTITY_CM = 3;

// A pasted copy lands this far down and to the right of the original, so it is
// visibly a second object rather than hiding exactly behind the first.
const PASTE_OFFSET_CM = 20;

// Floor area per seat below which a dining room is tighter than the service
// rules want. Full-service rooms are usually planned at 2.0-2.5 m2 per cover;
// under this figure the automatic arrangement starts refusing group bookings
// for want of an aisle.
const TIGHT_AREA_PER_SEAT_M2 = 2.0;

// Below this share of the canvas there is no room for an element's icon and
// name without them spilling over the fill.
const CAPTION_MIN_PERCENT = 3.2;

// Below this share of the canvas an entity is too small to carry the eight
// resize handles without them covering the whole body, which would leave no
// grabbable area to drag. Such entities get a single outset corner handle.
const COMPACT_HANDLE_PERCENT = 1.6;

const COLORS = {
  orange: "#ee754c",
  orangeDark: "#cf5b36",
  orangeSoft: "#fff4ef",
  ink: "#3f3537",
  text: "#61585a",
  muted: "#8d8586",
  line: "#e9e3e1",
  soft: "#faf8f6",
  cream: "#faf6e9",
  sage: "#99b0ad",
  sageDark: "#698783",
  danger: "#bd5454",
};

const OBJECT_LIBRARY = [
  { type: "kitchen", icon: "🍳", width: 300, height: 200, color: "#d9c2a7" },
  { type: "wc", icon: "🚻", width: 160, height: 160, color: "#abc9d8" },
  { type: "bar", icon: "🍸", width: 250, height: 90, color: "#b8a3c5" },
  { type: "door", icon: "🚪", width: 90, height: 20, color: "#b28c68" },
  { type: "wall", icon: "▬", width: 300, height: 15, color: "#777174" },
  { type: "window", icon: "▤", width: 150, height: 15, color: "#86bfd0" },
  { type: "stairs", icon: "⇅", width: 180, height: 240, color: "#b5aaa3" },
  { type: "reception", icon: "🔔", width: 180, height: 80, color: "#d8b777" },
  { type: "plant", icon: "🌿", width: 60, height: 60, color: "#83a77e" },
  { type: "custom", icon: "◇", width: 100, height: 100, color: "#99b0ad" },
];

const RESIZE_HANDLES = [
  { position: "nw", left: "0%", top: "0%", cursor: "nwse-resize" },
  { position: "n", left: "50%", top: "0%", cursor: "ns-resize" },
  { position: "ne", left: "100%", top: "0%", cursor: "nesw-resize" },
  { position: "e", left: "100%", top: "50%", cursor: "ew-resize" },
  { position: "se", left: "100%", top: "100%", cursor: "nwse-resize" },
  { position: "s", left: "50%", top: "100%", cursor: "ns-resize" },
  { position: "sw", left: "0%", top: "100%", cursor: "nesw-resize" },
  { position: "w", left: "0%", top: "50%", cursor: "ew-resize" },
];

const Page = styled.div`
  height: 100vh;
  min-height: 650px;
  margin: 0;
  width: 100%;
  color: ${COLORS.text};

  @media (max-width: 960px) {
    height: auto;
    min-height: 0;
  }
`;

const Shell = styled.section`
  display: flex;
  height: 100%;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: #fff;
  box-shadow: none;
`;

const Header = styled.header`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-bottom: 1px solid ${COLORS.line};
  background: #ffffff;

  @media (max-width: 820px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const HeaderCopy = styled.div`
  min-width: 170px;
`;

const Title = styled.h2`
  margin: 0;
  color: var(--app-orange, #ee754c);
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.025em;
`;

const Subtitle = styled.p`
  margin: 2px 0 0;
  color: ${COLORS.muted};
  font-size: 0.7rem;
`;

const HeaderTools = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;

  @media (max-width: 620px) {
    display: grid;
    grid-template-columns: 1fr 1fr;

    > :first-child {
      grid-column: 1 / -1;
    }
  }
`;

const RoomPicker = styled.div`
  min-width: 190px;

  label {
    display: block;
    margin-bottom: 2px;
    color: ${COLORS.ink};
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  select {
    min-height: 32px;
    border-color: ${COLORS.line};
    border-radius: 8px;
    color: ${COLORS.ink};
    font-size: 0.76rem;
  }
`;

const ToolbarButton = styled(Button)`
  min-height: 32px;
  padding: 5px 9px;
  border-radius: 7px;
  font-size: 0.7rem;
  font-weight: 750;
`;

const SaveButton = styled(OrangeButton)`
  min-height: 32px;
  padding-inline: 11px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 800;
`;

const Workspace = styled.div`
  display: grid;
  min-height: 0;
  flex: 1 1 auto;
  grid-template-columns: minmax(0, 1fr) 252px;
  overflow: hidden;

  @media (max-width: 960px) {
    min-height: 500px;
    grid-template-columns: 1fr;
    overflow: visible;
  }
`;

const StageColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: 10px 12px;
  background: #ffffff;

  @media (max-width: 620px) {
    padding: 10px;
  }
`;

const StageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 8px;
  margin-bottom: 7px;

  @media (max-width: 620px) {
    flex-wrap: wrap;
  }
`;

const DimensionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border: 1px solid #dce6e4;
  border-radius: 999px;
  background: #fff;
  color: ${COLORS.sageDark};
  font-size: 0.68rem;
  font-weight: 750;
`;

const Hint = styled.div`
  color: ${COLORS.muted};
  font-size: 0.67rem;
  text-align: right;
`;

const StageTools = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;

  @media (max-width: 620px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ZoomControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border: 1px solid ${COLORS.line};
  border-radius: 9px;
  background: #fff;
`;

const ZoomButton = styled.button`
  display: grid;
  min-width: 26px;
  height: 24px;
  padding: 0 5px;
  place-items: center;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${COLORS.text};
  font-size: 0.72rem;
  font-weight: 800;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${COLORS.orangeSoft};
    color: ${COLORS.orangeDark};
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const ZoomInputWrap = styled.label`
  display: inline-flex;
  align-items: center;
  height: 24px;
  border-radius: 6px;
  background: ${COLORS.soft};
  color: ${COLORS.text};
  font-size: 0.68rem;
  font-weight: 800;

  &:focus-within {
    box-shadow: 0 0 0 2px rgba(238, 117, 76, 0.22);
  }
`;

const ZoomInput = styled.input`
  width: 42px;
  height: 24px;
  padding: 0 2px 0 5px;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: right;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    margin: 0;
    appearance: none;
  }
`;

const ZoomSuffix = styled.span`
  padding-right: 5px;
`;

const CanvasViewport = styled.div`
  height: 100%;
  min-height: 285px;
  flex: 1 1 auto;
  overflow: auto;
  overscroll-behavior: none;
  padding: 0;
  cursor: ${({ $panning, $panReady }) =>
    $panning ? "grabbing" : $panReady ? "grab" : "default"};
  border: 0;
  border-radius: 0;
  background: #ffffff;

  scrollbar-color: ${COLORS.sage} transparent;
  scrollbar-width: thin;

  &:focus-visible {
    outline: 2px solid rgba(238, 117, 76, 0.55);
    outline-offset: -2px;
  }

  @media (max-width: 960px) {
    height: 410px;
    flex: 0 0 auto;
  }

  @media (max-width: 620px) {
    height: 330px;
    min-height: 260px;
    padding: 0;
  }
`;

const ZoomSurface = styled.div`
  width: ${({ $zoom }) => `${$zoom * 100}%`};
  min-width: 340px;
  margin: 0 auto;
  transition: width 0.16s ease;
`;

const CanvasFrame = styled.div`
  position: relative;
  width: 100%;
  padding: 16px 16px 28px 28px;
  border: 0;
  border-radius: 0;
  background: #fff;
  box-shadow: none;
`;

const HorizontalRuler = styled.div`
  position: absolute;
  left: 28px;
  right: 16px;
  bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.muted};
  font-size: 0.65rem;

  &::before,
  &::after {
    content: "";
    height: 1px;
    flex: 1;
    background: ${COLORS.line};
  }
`;

const VerticalRuler = styled.div`
  position: absolute;
  left: 4px;
  top: 16px;
  bottom: 28px;
  display: flex;
  align-items: center;
  color: ${COLORS.muted};
  font-size: 0.65rem;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;

const Canvas = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio};
  overflow: hidden;
  border: 1px solid #d7d1cd;
  border-radius: 0;
  background-color: #fff;
  background-image:
    linear-gradient(to right, rgba(153, 176, 173, 0.18) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(153, 176, 173, 0.18) 1px, transparent 1px);
  background-size: ${({ $gridX, $gridY }) => `${$gridX}% ${$gridY}%`};
  touch-action: none;
`;

const FloorPlan = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $opacity }) => $opacity};
  pointer-events: none;
  user-select: none;
`;

const CanvasEmpty = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 30px;
  color: ${COLORS.muted};
  text-align: center;
  font-size: 0.84rem;
  pointer-events: none;
`;

const EntityButton = styled.button`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: ${({ $width }) => $width}%;
  height: ${({ $height }) => $height}%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: grab;
  transform: rotate(${({ $rotation }) => $rotation}deg);
  transform-origin: center;
  touch-action: none;
  z-index: ${({ $kind }) => ($kind === "table" ? 4 : 2)};

  &:active {
    cursor: grabbing;
  }

  /* The box is drawn strictly to scale — a 3 cm wall must look 3 cm wide — so
     it carries no minimum size. This pad only widens the pointer target, which
     would otherwise be a few pixels across for thin objects. It is painted
     nowhere and takes no layout space, so the drawn scale stays honest. */
  &::before {
    content: "";
    position: absolute;
    inset: -6px;
    border-radius: 8px;
  }

  &::after {
    content: "";
    position: absolute;
    inset: -4px;
    border: 2px solid
      ${({ $selected }) => ($selected ? COLORS.orange : "transparent")};
    border-radius: 9px;
    box-shadow: ${({ $selected }) =>
      $selected ? "0 0 0 3px rgba(238, 117, 76, 0.14)" : "none"};
    pointer-events: none;
  }
`;

const ResizeHandle = styled.span`
  position: absolute;
  left: ${({ $left }) => $left};
  top: ${({ $top }) => $top};
  z-index: 12;
  width: 11px;
  height: 11px;
  border: 2px solid ${COLORS.orange};
  border-radius: 3px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(67, 48, 52, 0.2);
  cursor: ${({ $cursor }) => $cursor};
  /* Centred on the corner normally. On an entity only a few pixels across the
     handles would blanket the body and swallow every drag, so the compact one
     is pushed clear of the box instead. */
  transform: ${({ $compact }) =>
    $compact ? "translate(2px, 2px)" : "translate(-50%, -50%)"};
  touch-action: none;
`;

const RotateHandle = styled.span`
  position: absolute;
  left: 50%;
  top: -53px;
  z-index: 13;
  display: grid;
  width: 20px;
  height: 20px;
  place-items: center;
  border: 2px solid ${COLORS.orange};
  border-radius: 50%;
  background: #fff;
  color: ${COLORS.orangeDark};
  box-shadow: 0 1px 4px rgba(67, 48, 52, 0.2);
  cursor: grab;
  font-size: 0.68rem;
  font-weight: 900;
  line-height: 1;
  transform: translateX(-50%);
  touch-action: none;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 18px;
    width: 2px;
    height: 35px;
    background: ${COLORS.orange};
    transform: translateX(-50%);
  }
`;

const SizeBadge = styled.span`
  position: absolute;
  left: 50%;
  top: calc(100% + 13px);
  z-index: 10;
  padding: 4px 7px;
  border-radius: 6px;
  background: ${COLORS.ink};
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  line-height: 1;
  pointer-events: none;
  transform: translateX(-50%);
  white-space: nowrap;
`;

const TableVisualRoot = styled.div`
  position: absolute;
  inset: 0;
  filter: drop-shadow(0 1px 2px rgba(67, 48, 52, 0.16));
`;

const TableTop = styled.div`
  position: absolute;
  inset: ${({ $round }) => ($round ? "17%" : "18% 16%")};
  display: grid;
  place-items: center;
  padding: 0 6%;
  overflow: hidden;
  border-radius: ${({ $round }) => ($round ? "50%" : "14%")};
  background: ${({ $selected }) =>
    $selected
      ? `linear-gradient(155deg, ${COLORS.orange}, ${COLORS.orangeDark})`
      : "linear-gradient(155deg, #ffffff, #ece3d8)"};
  color: ${({ $selected }) => ($selected ? "#fff" : COLORS.ink)};
  font-size: clamp(0.5rem, 1.05vw, 0.8rem);
  font-weight: 850;
  letter-spacing: 0.01em;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* A hairline ring plus a top highlight reads as a solid surface at any size,
     and unlike a real border it never adds to the drawn footprint. */
  box-shadow:
    inset 0 0 0 1px
      ${({ $selected }) =>
        $selected ? "rgba(255, 255, 255, 0.45)" : "rgba(67, 48, 52, 0.16)"},
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
`;

const Chair = styled.span`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: clamp(4px, 15%, 13px);
  height: clamp(4px, 15%, 13px);
  /* Rounded at the back, squarer where it meets the table. */
  border-radius: 45% 45% 22% 22%;
  background: ${({ $selected }) =>
    $selected
      ? "linear-gradient(180deg, #ffb69b, #ef8963)"
      : "linear-gradient(180deg, #fdf9f5, #ddd2c6)"};
  box-shadow:
    inset 0 0 0 1px
      ${({ $selected }) =>
        $selected ? "rgba(207, 91, 54, 0.5)" : "rgba(67, 48, 52, 0.2)"},
    0 1px 1px rgba(67, 48, 52, 0.14);
  transform: translate(-50%, -50%) rotate(${({ $angle }) => $angle}deg);
`;

// Structural pieces read as parts of the building, not as furniture cards, so
// they are drawn as plain slabs with no icon or caption crowding them.
const SLAB_TYPES = new Set(["wall", "window", "door"]);

const slabSurface = (type) => {
  if (type === "window") {
    // Glazing bars.
    return `
      background-image: repeating-linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.55) 0 2px,
        rgba(255, 255, 255, 0) 2px 9px
      );
    `;
  }
  if (type === "door") {
    // A leaf with a lighter opening edge.
    return `
      background-image: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.16) 0 6%,
        rgba(255, 255, 255, 0.3) 6% 12%,
        rgba(255, 255, 255, 0) 12%
      );
    `;
  }
  return "";
};

const ObjectVisual = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 3%;
  overflow: hidden;
  border-radius: ${({ $type }) => {
    if ($type === "wall") return "1px";
    if (SLAB_TYPES.has($type)) return "2px";
    return "10px";
  }};
  /* Layering translucent light and shade over the chosen colour keeps every
     element in the same family whatever colour is picked, and lets an imported
     floor plan read faintly through the fill. */
  background-color: ${({ $color }) => $color || COLORS.sage};
  background-image: linear-gradient(
    158deg,
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 0) 52%,
    rgba(67, 48, 52, 0.14)
  );
  color: #fff;
  opacity: ${({ $opacity }) => $opacity};
  /* Inset ring rather than a border: a border would add to the drawn size and
     break the 1:1 scale for thin objects. */
  box-shadow:
    inset 0 0 0 1px rgba(67, 48, 52, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    0 1px 3px rgba(67, 48, 52, 0.14);
  ${({ $type }) => slabSurface($type)}

  span:first-child {
    font-size: clamp(0.6rem, 1.4vw, 1.3rem);
    line-height: 1;
    filter: drop-shadow(0 1px 1px rgba(67, 48, 52, 0.3));
  }

  span:last-child {
    max-width: 92%;
    overflow: hidden;
    font-size: clamp(0.44rem, 0.78vw, 0.68rem);
    font-weight: 850;
    letter-spacing: 0.02em;
    text-overflow: ellipsis;
    text-shadow: 0 1px 2px rgba(46, 33, 36, 0.45);
    white-space: nowrap;
  }
`;

const ObjectImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SidePanel = styled.aside`
  min-height: 0;
  overflow-y: auto;
  border-left: 1px solid ${COLORS.line};
  background: #fff;

  @media (max-width: 960px) {
    border-top: 1px solid ${COLORS.line};
    border-left: 0;
  }
`;

const PanelSection = styled.section`
  padding: 11px;
  border-bottom: 1px solid ${COLORS.line};
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 0 8px;
  color: ${COLORS.ink};
  font-size: 0.76rem;
  font-weight: 850;
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;

  .full {
    grid-column: 1 / -1;
  }

  label {
    margin-bottom: 2px;
    color: ${COLORS.muted};
    font-size: 0.68rem;
    font-weight: 750;
  }

  input,
  select {
    min-height: 32px;
    border-color: ${COLORS.line};
    border-radius: 7px;
    color: ${COLORS.ink};
    font-size: 0.78rem;
  }
`;

const UnitField = styled.div`
  position: relative;

  input {
    padding-right: 29px;
  }

  span {
    position: absolute;
    right: 9px;
    bottom: 9px;
    color: ${COLORS.muted};
    font-size: 0.65rem;
    pointer-events: none;
  }
`;

const UploadBox = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  min-height: 32px;
  padding: 6px;
  border: 1px dashed ${COLORS.sage};
  border-radius: 8px;
  background: #fbfdfc;
  color: ${COLORS.sageDark} !important;
  font-size: 0.74rem !important;
  font-weight: 800 !important;
  cursor: pointer;

  input {
    display: none;
  }
`;

const Library = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(108px, 1fr));
  gap: 8px;
`;

const LibraryButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 46px;
  padding: 6px 9px;
  overflow: hidden;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: #fff;
  color: ${COLORS.text};
  font-size: 0.68rem;
  font-weight: 800;
  text-align: left;
  cursor: pointer;
  transition: 0.14s ease;

  /* The element's own colour, so the palette doubles as a legend for what
     lands on the plan. */
  span:first-child {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: ${({ $swatch }) => $swatch || COLORS.sage};
    box-shadow:
      inset 0 0 0 1px rgba(67, 48, 52, 0.16),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    font-size: 0.86rem;
    line-height: 1;
  }

  &:hover:not(:disabled) {
    border-color: ${COLORS.orange};
    background: ${COLORS.orangeSoft};
    color: ${COLORS.orangeDark};
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

const Tray = styled.section`
  flex: 0 0 auto;
  max-height: 225px;
  overflow: hidden;
  border-top: 1px solid ${COLORS.line};
  background: #fff;

  @media (max-width: 980px) {
    max-height: none;
    overflow: visible;
  }
`;

const DockTabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 0 12px;
  border-bottom: 1px solid ${COLORS.line};
  background: ${COLORS.soft};
`;

const DockTab = styled.button`
  min-width: 112px;
  padding: 7px 12px 6px;
  border: 0;
  border-bottom: 3px solid
    ${({ $active }) => ($active ? COLORS.orange : "transparent")};
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${({ $active }) => ($active ? COLORS.orangeDark : COLORS.muted)};
  font-size: 0.72rem;
  font-weight: 850;
  cursor: pointer;
`;

const DockBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  height: 184px;
  min-height: 0;

  @media (max-width: 980px) {
    height: auto;
  }
`;

const DockMain = styled.div`
  min-width: 0;
  overflow: auto;
  padding: 10px 12px;
`;

const TrayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const TrayTitle = styled.div`
  color: ${COLORS.ink};
  font-size: 0.74rem;
  font-weight: 850;

  span {
    margin-left: 7px;
    color: ${COLORS.muted};
    font-size: 0.64rem;
    font-weight: 650;
  }
`;

const TrayList = styled.div`
  display: flex;
  align-items: stretch;
  gap: 7px;
  overflow-x: auto;
  padding: 2px 2px 7px;
`;

const TrayCard = styled.button`
  width: 90px;
  min-width: 90px;
  min-height: 80px;
  padding: 5px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: ${COLORS.soft};
  color: ${COLORS.ink};
  cursor: pointer;
  transition: 0.14s ease;

  &:hover {
    border-color: ${COLORS.orange};
    background: ${COLORS.orangeSoft};
    transform: translateY(-1px);
  }
`;

const MiniTableWrap = styled.div`
  position: relative;
  width: 56px;
  height: 42px;
  margin: 0 auto 3px;
`;

const TrayLabel = styled.div`
  overflow: hidden;
  font-size: 0.7rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrayMeta = styled.div`
  margin-top: 2px;
  color: ${COLORS.muted};
  font-size: 0.62rem;
`;

const EmptyTray = styled.div`
  width: 100%;
  padding: 15px;
  border: 1px dashed ${COLORS.line};
  border-radius: 9px;
  color: ${COLORS.muted};
  font-size: 0.76rem;
  text-align: center;
`;

const Status = styled.div`
  padding: 8px 20px;
  border-top: 1px solid ${COLORS.line};
  background: ${({ $error }) => ($error ? "#fff5f5" : "#f4faf8")};
  color: ${({ $error }) => ($error ? COLORS.danger : COLORS.sageDark)};
  font-size: 0.74rem;
  font-weight: 700;
`;

const TitleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;

const ActionLink = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.sageDark};
  font-size: 0.7rem;
  font-weight: 800;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${COLORS.orangeDark};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/* Bootstrap's Form.Check type="switch" rendered as a bare grey knob here —
   the .form-switch track never appeared and the caption collapsed underneath
   it. This uses the app's own switch and lays the wording out beside it. */
const LockRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const LockText = styled.label`
  display: block;
  margin: 0;
  cursor: pointer;

  b {
    display: block;
    color: ${COLORS.ink};
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1.25;
  }

  span {
    display: block;
    margin-top: 2px;
    color: ${COLORS.muted};
    font-size: 0.64rem;
    font-weight: 600;
    line-height: 1.35;
  }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
  margin-bottom: 8px;
`;

const ReviewStat = styled.div`
  padding: 7px 9px;
  border: 1px solid ${({ $warn }) => ($warn ? "#f0c9b8" : COLORS.line)};
  border-radius: 9px;
  background: ${({ $warn }) => ($warn ? COLORS.orangeSoft : COLORS.soft)};

  b {
    display: block;
    color: ${({ $warn }) => ($warn ? COLORS.orangeDark : COLORS.ink)};
    font-size: 0.92rem;
    font-weight: 850;
    line-height: 1.1;
  }

  span {
    color: ${COLORS.muted};
    font-size: 0.62rem;
    font-weight: 700;
  }
`;

const ReviewToggle = styled.button`
  flex: 0 0 auto;
  padding: 3px 8px;
  border: 1px solid ${COLORS.line};
  border-radius: 8px;
  background: ${COLORS.soft};
  color: ${COLORS.orangeDark};
  font-size: 0.68rem;
  font-weight: 800;
  cursor: pointer;
  transition: 0.14s ease;

  &:hover {
    border-color: ${COLORS.orange};
    background: ${COLORS.orangeSoft};
  }
`;

const ReviewNote = styled.p`
  margin: 0 0 6px;
  padding: 6px 8px;
  border-left: 3px solid
    ${({ $warn, $ok }) => {
      if ($warn) return COLORS.orange;
      if ($ok) return COLORS.sage;
      return COLORS.line;
    }};
  border-radius: 0 7px 7px 0;
  background: ${({ $warn, $ok }) => {
    if ($warn) return COLORS.orangeSoft;
    if ($ok) return "#f2f7f6";
    return COLORS.soft;
  }};
  color: ${COLORS.text};
  font-size: 0.68rem;
  font-weight: 650;
  line-height: 1.4;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DangerLink = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.danger};
  font-size: 0.7rem;
  font-weight: 800;
  cursor: pointer;
`;

const numberOr = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// Positions and sizes are stored as DecimalField(decimal_places=2) on the API.
// Drag/resize maths produce long floats, which the serializer rejects, so every
// centimetre value has to be rounded before it reaches state or the payload.
const round2 = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

// On-screen footprint of a box after CSS `rotate()`, which turns it about its
// centre. A 1329 x 10 cm wall turned 90 degrees occupies 10 x 1329 cm on the
// plan even though its layout box is still 1329 wide.
const rotatedExtent = (width, height, rotation) => {
  const radians = ((Number(rotation) || 0) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
};

// Keep an entity inside the room along one axis.
//
// `value` is the unrotated box edge, which is what left/top actually store, and
// `extent` is the footprint that has to fit between the walls. Rotation moves
// neither the centre nor left/top, so the bound is derived from the centre:
// centre must stay in [extent/2, roomSize - extent/2], and left = centre -
// size/2. With no rotation extent === size and this reduces to the obvious
// [0, roomSize - size].
//
// Two cases need the range to run backwards. An entity longer than the room has
// a negative upper bound, and a rotated one has a negative lower bound because
// its layout box legitimately overhangs the wall while its footprint does not.
// The naive `clamp(value, 0, roomSize - size)` collapsed both to a single point
// — 0 for the oversized case, pinning it in the corner, and for the rotated
// wall an upper bound far short of the right wall it could plainly still reach.
const clampToRoom = (value, size, roomSize, extent = size) => {
  const low = (extent - size) / 2;
  const high = roomSize - (extent + size) / 2;
  return clamp(value, Math.min(low, high), Math.max(low, high));
};

const isPlaced = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

// A detached copy of an element's properties, so the copy buffer survives the
// original being edited, moved, or deleted.
const snapshotObject = (item) => ({
  items_name: item.items_name,
  item_type: item.item_type,
  width: item.width,
  height: item.height,
  left: item.left,
  top: item.top,
  color: item.color,
  opacity: item.opacity,
  rotation: item.rotation,
});

// DRF reports validation problems as { field: ["message"] }, which never shows
// up under `detail`/`error`. Without this the UI silently falls back to the
// generic message and the real reason for a failed save stays hidden.
const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data || fallback;
  if (data.detail) return String(data.detail);
  if (data.error) return String(data.error);

  const fieldErrors = Object.entries(data)
    .map(([field, messages]) => {
      const text = Array.isArray(messages)
        ? messages.join(" ")
        : String(messages);
      return text ? `${field}: ${text}` : "";
    })
    .filter(Boolean);

  return fieldErrors.length ? fieldErrors.join(" · ") : fallback;
};

const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (/^(blob:|data:|https?:)/i.test(url)) return url;
  if (typeof window === "undefined") return url;
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

const roomDimensions = (room) => ({
  width: numberOr(room?.width, DEFAULT_ROOM_WIDTH_M),
  height: numberOr(room?.height, DEFAULT_ROOM_HEIGHT_M),
  opacity: clamp(numberOr(room?.floor_plan_opacity, 0.35), 0.05, 1),
});

const normalizeTable = (table, room) => {
  const dimensions = roomDimensions(room);
  const roomWidthCm = dimensions.width * 100;
  const roomHeightCm = dimensions.height * 100;
  const realWidth = numberOr(table.real_width_cm, 80);
  const realHeight = numberOr(table.real_height_cm, 80);
  const hasPhysicalLeft = ![null, undefined, ""].includes(table.left_cm);
  const hasPhysicalTop = ![null, undefined, ""].includes(table.top_cm);
  const left = Number(table.left_cm);
  const top = Number(table.top_cm);

  return {
    ...table,
    real_width_cm: realWidth,
    real_height_cm: realHeight,
    left_cm: hasPhysicalLeft && Number.isFinite(left)
      ? left
      : (numberOr(table.left, 0) / LEGACY_CANVAS_WIDTH) * roomWidthCm,
    top_cm: hasPhysicalTop && Number.isFinite(top)
      ? top
      : (numberOr(table.top, 0) / LEGACY_CANVAS_HEIGHT) * roomHeightCm,
    rotation: Number(table.rotation) || 0,
    isPlaced: isPlaced(table.isPlaced),
    is_locked: isPlaced(table.is_locked),
  };
};

const getChairPositions = (seatCount, round) => {
  const count = clamp(Number(seatCount) || 4, 1, 12);

  if (round) {
    return Array.from({ length: Math.min(count, 8) }, (_, index) => {
      const angle = (index / Math.min(count, 8)) * Math.PI * 2 - Math.PI / 2;
      return {
        left: 50 + Math.cos(angle) * 42,
        top: 50 + Math.sin(angle) * 42,
        angle: (angle * 180) / Math.PI + 90,
      };
    });
  }

  const positions = [];
  const sides = ["top", "bottom", "left", "right"];
  const sideCounts = sides.map((_, sideIndex) =>
    Math.floor(count / 4) + (sideIndex < count % 4 ? 1 : 0),
  );

  sides.forEach((side, sideIndex) => {
    const sideCount = sideCounts[sideIndex];
    for (let index = 0; index < sideCount; index += 1) {
      const progress = ((index + 1) / (sideCount + 1)) * 64 + 18;
      if (side === "top") positions.push({ left: progress, top: 8, angle: 0 });
      if (side === "bottom") positions.push({ left: progress, top: 92, angle: 0 });
      if (side === "left") positions.push({ left: 8, top: progress, angle: 90 });
      if (side === "right") positions.push({ left: 92, top: progress, angle: 90 });
    }
  });

  return positions;
};

const TableVisual = memo(({ table, selected = false }) => {
  const round = String(table?.round_or_square).toLowerCase() === "round";
  const chairs = useMemo(
    () => getChairPositions(table?.number_of_seats, round),
    [table?.number_of_seats, round],
  );

  return (
    <TableVisualRoot>
      {chairs.map((chair, index) => (
        <Chair
          key={`${table?.id || "table"}-chair-${index}`}
          $left={chair.left}
          $top={chair.top}
          $angle={chair.angle}
          $selected={selected}
        />
      ))}
      <TableTop $round={round} $selected={selected}>
        {table?.table_name || table?.table_number || "—"}
      </TableTop>
    </TableVisualRoot>
  );
});

const RoomLayoutEditor = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const dragRef = useRef(null);

  const { zones = [] } = useSelector((state) => state.restaurantZones);
  const { tables: serverTables = [] } = useSelector(
    (state) => state.restaurantTables,
  );
  const { restaurants } = useSelector((state) => state.restaurantDetails);
  const { userInfo } = useSelector((state) => state.userLogin);

  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [draftTables, setDraftTables] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dockTab, setDockTab] = useState("tables");
  const [zoom, setZoom] = useState(1);
  const [roomDraft, setRoomDraft] = useState({
    width: DEFAULT_ROOM_WIDTH_M,
    height: DEFAULT_ROOM_HEIGHT_M,
    opacity: 0.35,
  });
  const [floorPlanFile, setFloorPlanFile] = useState(null);
  const [floorPlanPreview, setFloorPlanPreview] = useState("");
  const [removeFloorPlan, setRemoveFloorPlan] = useState(false);
  const [isLoadingObjects, setIsLoadingObjects] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingObject, setIsAddingObject] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusError, setStatusError] = useState(false);
  // Copy buffer for Ctrl+C / Ctrl+V. It holds a snapshot of the element's
  // properties rather than a reference, so pasting still works after the
  // original has been edited, moved, or deleted.
  const [copiedObject, setCopiedObject] = useState(null);
  // The room review's detail is collapsed by default: the two headline figures
  // are what someone glances at, and six warnings underneath push the rest of
  // the panel off screen.
  const [showReviewDetail, setShowReviewDetail] = useState(false);
  const [spacePanReady, setSpacePanReady] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef(null);

  const [showNewRoom, setShowNewRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: "", width: 10, height: 6 });
  const [showDeleteRoom, setShowDeleteRoom] = useState(false);
  const [showAddTables, setShowAddTables] = useState(false);
  const [newTables, setNewTables] = useState({
    count: 1,
    seats: 4,
    shape: "square",
    width: 80,
    height: 80,
  });

  const authConfig = useMemo(
    () => ({
      headers: userInfo?.token
        ? { Authorization: `Bearer ${userInfo.token}` }
        : {},
    }),
    [userInfo?.token],
  );

  const selectedRoom = useMemo(
    () => zones.find((room) => Number(room.id) === Number(selectedRoomId)),
    [zones, selectedRoomId],
  );

  const roomWidthM = numberOr(roomDraft.width, DEFAULT_ROOM_WIDTH_M);
  const roomHeightM = numberOr(roomDraft.height, DEFAULT_ROOM_HEIGHT_M);
  const roomWidthCm = roomWidthM * 100;
  const roomHeightCm = roomHeightM * 100;

  const roomTables = useMemo(
    () =>
      draftTables.filter(
        (table) => Number(table.place_of_table) === Number(selectedRoomId),
      ),
    [draftTables, selectedRoomId],
  );

  // A running critique of the room as drawn. Built from the draft rather than
  // the saved rows, so it answers while the layout is still being changed.
  const roomReview = useMemo(() => {
    if (!selectedRoom) return null;

    return reviewRoom({
      room: { width: roomWidthCm, height: roomHeightCm },
      tables: draftTables
        .filter((table) => isPlaced(table.isPlaced))
        .map((table) => ({
          id: table.id,
          table_name: table.table_name,
          table_number: table.table_number,
          number_of_seats: table.number_of_seats,
          left: Number(table.left_cm) || 0,
          top: Number(table.top_cm) || 0,
          width: numberOr(table.real_width_cm, 80),
          height: numberOr(table.real_height_cm, 80),
          rotation: Number(table.rotation) || 0,
        })),
      elements: objects.map((item) => ({
        item_type: item.item_type,
        items_name: item.items_name,
        left: Number(item.left) || 0,
        top: Number(item.top) || 0,
        width: numberOr(item.width, 100),
        height: numberOr(item.height, 100),
        rotation: Number(item.rotation) || 0,
      })),
    });
  }, [selectedRoom, roomWidthCm, roomHeightCm, draftTables, objects]);

  // How many things the review has to say. Drives the "ver mais" label and
  // decides whether the detail needs hiding at all.
  const reviewIssueCount = useMemo(() => {
    if (!roomReview) return 0;

    const shellTooSmall =
      roomReview.usableAreaM2 != null &&
      roomReview.usableAreaM2 / roomReview.canvasAreaM2 < 0.6;
    const tooTight =
      roomReview.areaPerSeatM2 != null &&
      roomReview.areaPerSeatM2 < TIGHT_AREA_PER_SEAT_M2;

    return (
      (shellTooSmall ? 1 : 0) +
      (tooTight ? 1 : 0) +
      (roomReview.crowdedTables.length ? 1 : 0) +
      (roomReview.collidingTables.length ? 1 : 0) +
      (roomReview.outsideTables.length ? 1 : 0) +
      (roomReview.unreachablePlaces.length ? 1 : 0) +
      (roomReview.strayElements.length ? 1 : 0)
    );
  }, [roomReview]);

  const placedTables = useMemo(
    () => roomTables.filter((table) => isPlaced(table.isPlaced)),
    [roomTables],
  );

  const unplacedTables = useMemo(
    () => roomTables.filter((table) => !isPlaced(table.isPlaced)),
    [roomTables],
  );

  const selectedEntity = useMemo(() => {
    if (!selected) return null;
    if (selected.kind === "table") {
      return draftTables.find((table) => Number(table.id) === Number(selected.id));
    }
    return objects.find((item) => Number(item.id) === Number(selected.id));
  }, [selected, draftTables, objects]);

  useEffect(() => {
    if (zones.length && !selectedRoomId) {
      setSelectedRoomId(zones[0].id);
    } else if (
      selectedRoomId &&
      !zones.some((room) => Number(room.id) === Number(selectedRoomId))
    ) {
      setSelectedRoomId(zones[0]?.id || "");
    }
  }, [zones, selectedRoomId]);

  useEffect(() => {
    setDraftTables(
      (serverTables || []).map((table) => {
        const room = zones.find(
          (candidate) => Number(candidate.id) === Number(table.place_of_table),
        );
        return normalizeTable(table, room);
      }),
    );
  }, [serverTables, zones]);

  useEffect(() => {
    if (!selectedRoom) return;
    setRoomDraft(roomDimensions(selectedRoom));
    setFloorPlanFile(null);
    setFloorPlanPreview("");
    setRemoveFloorPlan(false);
    setSelected(null);
    setDockTab("tables");
    setZoom(1);
    setStatusMessage("");
  }, [selectedRoom]);

  const loadObjects = useCallback(async () => {
    if (!restaurants?.id || !selectedRoomId || !userInfo?.token) {
      setObjects([]);
      return;
    }

    try {
      setIsLoadingObjects(true);
      const { data } = await api.get(
        `/restaurants/${restaurants.id}/design-items/?place_of_table=${selectedRoomId}`,
        authConfig,
      );
      setObjects(data || []);
    } catch (error) {
      setStatusError(true);
      setStatusMessage(
        apiErrorMessage(error, t("roomDesigner.errors.loadObjects")),
      );
    } finally {
      setIsLoadingObjects(false);
    }
  }, [restaurants?.id, selectedRoomId, userInfo?.token, authConfig, t]);

  useEffect(() => {
    loadObjects();
  }, [loadObjects]);

  useEffect(() => {
    return () => {
      if (floorPlanPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(floorPlanPreview);
      }
    };
  }, [floorPlanPreview]);

  const updateTable = useCallback((tableId, patch) => {
    setDraftTables((current) =>
      current.map((table) =>
        Number(table.id) === Number(tableId) ? { ...table, ...patch } : table,
      ),
    );
  }, []);

  const updateObject = useCallback((objectId, patch) => {
    setObjects((current) =>
      current.map((item) =>
        Number(item.id) === Number(objectId) ? { ...item, ...patch } : item,
      ),
    );
  }, []);

  const selectEntity = useCallback((kind, id) => {
    setSelected({ kind, id });
    setDockTab(kind === "table" ? "tables" : "objects");
  }, []);

  const startDrag = (event, kind, entity) => {
    if (!canvasRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    selectEntity(kind, entity.id);
    dragRef.current = {
      mode: "move",
      kind,
      id: entity.id,
      startX: event.clientX,
      startY: event.clientY,
      left: Number(kind === "table" ? entity.left_cm : entity.left) || 0,
      top: Number(kind === "table" ? entity.top_cm : entity.top) || 0,
      width: numberOr(
        kind === "table" ? entity.real_width_cm : entity.width,
        80,
      ),
      height: numberOr(
        kind === "table" ? entity.real_height_cm : entity.height,
        80,
      ),
      rotation: Number(entity.rotation) || 0,
      // clientWidth/Height is the padding box, which is what the entities'
      // percentage left/top/width resolve against. getBoundingClientRect()
      // adds the canvas border and makes every drag drift by ~0.3%.
      rect: {
        width: canvasRef.current.clientWidth,
        height: canvasRef.current.clientHeight,
      },
      roomWidthCm,
      roomHeightCm,
    };
  };

  const startResize = (event, kind, entity, handle) => {
    if (!canvasRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    selectEntity(kind, entity.id);
    dragRef.current = {
      mode: "resize",
      handle,
      kind,
      id: entity.id,
      startX: event.clientX,
      startY: event.clientY,
      left: Number(kind === "table" ? entity.left_cm : entity.left) || 0,
      top: Number(kind === "table" ? entity.top_cm : entity.top) || 0,
      width: numberOr(
        kind === "table" ? entity.real_width_cm : entity.width,
        80,
      ),
      height: numberOr(
        kind === "table" ? entity.real_height_cm : entity.height,
        80,
      ),
      rotation: Number(entity.rotation) || 0,
      // clientWidth/Height is the padding box, which is what the entities'
      // percentage left/top/width resolve against. getBoundingClientRect()
      // adds the canvas border and makes every drag drift by ~0.3%.
      rect: {
        width: canvasRef.current.clientWidth,
        height: canvasRef.current.clientHeight,
      },
      roomWidthCm,
      roomHeightCm,
    };
  };

  const startRotate = (event, kind, entity) => {
    const entityElement = event.currentTarget.closest("[data-entity-kind]");
    if (!entityElement) return;
    event.preventDefault();
    event.stopPropagation();
    selectEntity(kind, entity.id);
    const bounds = entityElement.getBoundingClientRect();
    const centerX = bounds.left + bounds.width / 2;
    const centerY = bounds.top + bounds.height / 2;
    dragRef.current = {
      mode: "rotate",
      kind,
      id: entity.id,
      centerX,
      centerY,
      startAngle: Math.atan2(event.clientY - centerY, event.clientX - centerX),
      rotation: Number(entity.rotation) || 0,
    };
  };

  const handleCanvasWheel = useCallback((event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    event.stopPropagation();

    const viewport = viewportRef.current;
    if (!viewport) return;
    const bounds = viewport.getBoundingClientRect();
    const offsetX = event.clientX - bounds.left;
    const offsetY = event.clientY - bounds.top;
    const contentX = viewport.scrollLeft + offsetX;
    const contentY = viewport.scrollTop + offsetY;
    const direction = event.deltaY < 0 ? 0.1 : -0.1;

    setZoom((current) => {
      const next = clamp(Math.round((current + direction) * 100) / 100, 0.25, 5);
      const scale = next / current;
      requestAnimationFrame(() => {
        viewport.scrollLeft = contentX * scale - offsetX;
        viewport.scrollTop = contentY * scale - offsetY;
      });
      return next;
    });
  }, []);

  const handleCanvasKeyDown = useCallback((event) => {
    if (!event.ctrlKey && !event.metaKey) return;

    const zoomIn = ["+", "=", "Add"].includes(event.key);
    const zoomOut = ["-", "_", "Subtract"].includes(event.key);
    const resetZoom = event.key === "0";
    if (!zoomIn && !zoomOut && !resetZoom) return;

    event.preventDefault();
    event.stopPropagation();
    if (resetZoom) {
      setZoom(1);
    } else {
      setZoom((current) =>
        clamp(current + (zoomIn ? 0.25 : -0.25), 0.25, 5),
      );
    }
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    viewport.addEventListener("wheel", handleCanvasWheel, { passive: false });
    viewport.addEventListener("keydown", handleCanvasKeyDown);
    return () => {
      viewport.removeEventListener("wheel", handleCanvasWheel);
      viewport.removeEventListener("keydown", handleCanvasKeyDown);
    };
  }, [handleCanvasKeyDown, handleCanvasWheel, selectedRoomId]);

  // Photoshop-style panning: hold space and drag, or drag with the middle
  // mouse button. The viewport is already a scroll container, so panning is
  // just moving scrollLeft/scrollTop against the pointer delta.
  useEffect(() => {
    const isTypingTarget = (target) => {
      const tag = target?.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      );
    };

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      if (isTypingTarget(event.target)) return;

      const viewport = viewportRef.current;
      if (!viewport) return;

      // Only claim space when the canvas is actually the focus of attention,
      // so space still activates focused buttons everywhere else.
      const hovered = viewport.matches(":hover");
      const focused = viewport.contains(document.activeElement);
      if (!hovered && !focused) return;

      // Stop the page from scrolling while space is used as a pan modifier.
      event.preventDefault();
      setSpacePanReady(true);
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      setSpacePanReady(false);
    };

    // Alt-tabbing away while space is held would otherwise leave it stuck.
    const handleBlur = () => setSpacePanReady(false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  const startPan = (event) => {
    const viewport = viewportRef.current;
    if (!viewport) return false;

    const middleButton = event.button === 1;
    if (!middleButton && !(spacePanReady && event.button === 0)) return false;

    event.preventDefault();
    event.stopPropagation();

    panRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
    };
    setIsPanning(true);
    return true;
  };

  useEffect(() => {
    if (!isPanning) return undefined;

    const handlePointerMove = (event) => {
      const pan = panRef.current;
      const viewport = viewportRef.current;
      if (!pan || !viewport) return;
      if (pan.pointerId !== undefined && event.pointerId !== pan.pointerId) return;

      event.preventDefault();
      viewport.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX);
      viewport.scrollTop = pan.scrollTop - (event.clientY - pan.startY);
    };

    const endPan = () => {
      panRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: false });
    window.addEventListener("pointerup", endPan);
    window.addEventListener("pointercancel", endPan);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", endPan);
      window.removeEventListener("pointercancel", endPan);
    };
  }, [isPanning]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;

      if (drag.mode === "rotate") {
        const angle = Math.atan2(
          event.clientY - drag.centerY,
          event.clientX - drag.centerX,
        );
        const angleDelta = ((angle - drag.startAngle) * 180) / Math.PI;
        const snap = event.shiftKey ? 15 : 1;
        const rotation =
          (Math.round((drag.rotation + angleDelta) / snap) * snap + 360) % 360;

        if (drag.kind === "table") {
          updateTable(drag.id, { rotation });
        } else {
          updateObject(drag.id, { rotation });
        }
        return;
      }

      const deltaX = ((event.clientX - drag.startX) / drag.rect.width) * drag.roomWidthCm;
      const deltaY = ((event.clientY - drag.startY) / drag.rect.height) * drag.roomHeightCm;

      if (drag.mode === "resize") {
        let left = drag.left;
        let top = drag.top;
        let width = drag.width;
        let height = drag.height;

        if (drag.handle.includes("e")) {
          width = Math.max(MIN_ENTITY_CM, drag.width + deltaX);
        }
        if (drag.handle.includes("w")) {
          left = Math.min(
            drag.left + deltaX,
            drag.left + drag.width - MIN_ENTITY_CM,
          );
          width = drag.width + (drag.left - left);
        }
        if (drag.handle.includes("s")) {
          height = Math.max(MIN_ENTITY_CM, drag.height + deltaY);
        }
        if (drag.handle.includes("n")) {
          top = Math.min(
            drag.top + deltaY,
            drag.top + drag.height - MIN_ENTITY_CM,
          );
          height = drag.height + (drag.top - top);
        }

        if (drag.kind === "table") {
          updateTable(drag.id, {
            left_cm: round2(left),
            top_cm: round2(top),
            real_width_cm: round2(width),
            real_height_cm: round2(height),
            isPlaced: true,
          });
        } else {
          updateObject(drag.id, {
            left: round2(left),
            top: round2(top),
            width: round2(width),
            height: round2(height),
          });
        }
        return;
      }

      const extent = rotatedExtent(drag.width, drag.height, drag.rotation);
      const left = round2(
        clampToRoom(
          drag.left + deltaX,
          drag.width,
          drag.roomWidthCm,
          extent.width,
        ),
      );
      const top = round2(
        clampToRoom(
          drag.top + deltaY,
          drag.height,
          drag.roomHeightCm,
          extent.height,
        ),
      );

      if (drag.kind === "table") {
        updateTable(drag.id, { left_cm: left, top_cm: top, isPlaced: true });
      } else {
        updateObject(drag.id, { left, top });
      }
    };

    const handlePointerUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [updateObject, updateTable]);

  const placeTable = (table) => {
    const width = numberOr(table.real_width_cm, 80);
    const height = numberOr(table.real_height_cm, 80);
    updateTable(table.id, {
      isPlaced: true,
      left_cm: round2(clamp(roomWidthCm / 2 - width / 2, 0, roomWidthCm - width)),
      top_cm: round2(
        clamp(roomHeightCm / 2 - height / 2, 0, roomHeightCm - height),
      ),
    });
    selectEntity("table", table.id);
  };

  const removeTableFromRoom = (table) => {
    updateTable(table.id, { isPlaced: false, left_cm: 0, top_cm: 0 });
    setSelected(null);
  };

  const createObject = async (payload, fallbackMessageKey) => {
    if (!restaurants?.id || !selectedRoomId) return null;
    try {
      setIsAddingObject(true);
      const { data } = await api.post(
        `/restaurants/${restaurants.id}/design-items/`,
        { ...payload, restaurant: restaurants.id, place_of_table: selectedRoomId },
        authConfig,
      );
      setObjects((current) => [...current, data]);
      selectEntity("object", data.id);
      return data;
    } catch (error) {
      setStatusError(true);
      setStatusMessage(apiErrorMessage(error, t(fallbackMessageKey)));
      return null;
    } finally {
      setIsAddingObject(false);
    }
  };

  const addObject = (definition) =>
    createObject(
      {
        items_name: t(`roomDesigner.objects.${definition.type}`),
        item_type: definition.type,
        width: definition.width,
        height: definition.height,
        left: round2(
          clamp(roomWidthCm / 2 - definition.width / 2, 0, roomWidthCm),
        ),
        top: round2(
          clamp(roomHeightCm / 2 - definition.height / 2, 0, roomHeightCm),
        ),
        color: definition.color,
        opacity: 1,
        rotation: 0,
      },
      "roomDesigner.errors.addObject",
    );

  // Paste a snapshot as a new element, nudged clear of the original and kept
  // inside the room. Rotation is preserved, so the copy of a turned wall lines
  // up with the wall it came from.
  const pasteObject = (snapshot) => {
    if (!snapshot) return null;

    const width = numberOr(snapshot.width, 100);
    const height = numberOr(snapshot.height, 100);
    const rotation = Math.round(Number(snapshot.rotation) || 0);
    const extent = rotatedExtent(width, height, rotation);

    return createObject(
      {
        items_name: snapshot.items_name,
        item_type: snapshot.item_type,
        width,
        height,
        left: round2(
          clampToRoom(
            round2(snapshot.left) + PASTE_OFFSET_CM,
            width,
            roomWidthCm,
            extent.width,
          ),
        ),
        top: round2(
          clampToRoom(
            round2(snapshot.top) + PASTE_OFFSET_CM,
            height,
            roomHeightCm,
            extent.height,
          ),
        ),
        color: snapshot.color,
        opacity: round2(clamp(Number(snapshot.opacity) || 1, 0.1, 1)),
        rotation,
      },
      "roomDesigner.errors.duplicateObject",
    );
  };

  // The button in the side panel: copy and paste in one step, without
  // disturbing whatever is already in the copy buffer.
  const duplicateSelectedObject = () => {
    if (selected?.kind !== "object" || !selectedEntity) return;
    pasteObject(snapshotObject(selectedEntity));
  };

  const deleteSelectedEntityRef = useRef(null);
  const removeTableFromRoomRef = useRef(null);
  const pasteObjectRef = useRef(null);

  const deleteSelectedEntity = async () => {
    if (!selectedEntity || !restaurants?.id) return;
    try {
      if (selected?.kind === "object") {
        await api.delete(
          `/restaurants/${restaurants.id}/design-items/${selectedEntity.id}/`,
          authConfig,
        );
        setObjects((current) =>
          current.filter((item) => Number(item.id) !== Number(selectedEntity.id)),
        );
      } else {
        await api.delete(
          `/restaurants/${restaurants.id}/tables/${selectedEntity.id}/`,
          authConfig,
        );
        await dispatch(fetchTables(restaurants.id));
      }
      setSelected(null);
    } catch (error) {
      setStatusError(true);
      setStatusMessage(
        apiErrorMessage(error, t("roomDesigner.errors.deleteItem")),
      );
    }
  };

  deleteSelectedEntityRef.current = deleteSelectedEntity;
  removeTableFromRoomRef.current = removeTableFromRoom;
  pasteObjectRef.current = pasteObject;

  // Editor keyboard shortcuts.
  //
  // Delete removes the selected element outright, but sends the selected table
  // back to the tray rather than deleting it — a table carries reservations and
  // a number, so losing it to a keystroke would be destructive in a way that
  // removing a wall is not.
  //
  // Ctrl/Cmd+C and Ctrl/Cmd+V copy and paste elements. Tables are deliberately
  // left out: each one needs its own number and carries its own bookings, so
  // they are added through "Adicionar mesas" instead.
  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }

      const accel = event.ctrlKey || event.metaKey;
      const key = event.key?.toLowerCase();

      if (accel && key === "c") {
        if (selected?.kind !== "object" || !selectedEntity) return;
        // Let a real text selection win over copying the element.
        if (!window.getSelection?.().isCollapsed) return;
        event.preventDefault();
        setCopiedObject(snapshotObject(selectedEntity));
        setStatusError(false);
        setStatusMessage(t("roomDesigner.copied"));
        return;
      }

      if (accel && key === "v") {
        if (!copiedObject || !selectedRoomId) return;
        event.preventDefault();
        pasteObjectRef.current?.(copiedObject);
        return;
      }

      if (event.key === "Delete") {
        if (!selected || !selectedEntity) return;
        event.preventDefault();

        if (selected.kind === "object") {
          deleteSelectedEntityRef.current?.();
        } else {
          removeTableFromRoomRef.current?.(selectedEntity);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selected, selectedEntity, copiedObject, selectedRoomId, t]);

  const handleFloorPlan = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (floorPlanPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(floorPlanPreview);
    }
    setFloorPlanFile(file);
    setFloorPlanPreview(URL.createObjectURL(file));
    setRemoveFloorPlan(false);
  };

  const clearFloorPlan = () => {
    if (floorPlanPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(floorPlanPreview);
    }
    setFloorPlanFile(null);
    setFloorPlanPreview("");
    setRemoveFloorPlan(true);
  };

  const saveLayout = async () => {
    if (!restaurants?.id || !selectedRoom) return;
    try {
      setIsSaving(true);
      setStatusError(false);
      setStatusMessage("");

      if (floorPlanFile) {
        const formData = new FormData();
        formData.append("width", String(roomWidthM));
        formData.append("height", String(roomHeightM));
        formData.append("floor_plan_opacity", String(roomDraft.opacity));
        formData.append("floor_plan_image", floorPlanFile);
        await api.patch(
          `/restaurants/${restaurants.id}/placeoftable/${selectedRoom.id}/`,
          formData,
          {
            headers: {
              ...authConfig.headers,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        await api.patch(
          `/restaurants/${restaurants.id}/placeoftable/${selectedRoom.id}/`,
          {
            width: roomWidthM,
            height: roomHeightM,
            floor_plan_opacity: roomDraft.opacity,
            ...(removeFloorPlan ? { floor_plan_image: null } : {}),
          },
          authConfig,
        );
      }

      const legacyScale = Math.min(
        LEGACY_CANVAS_WIDTH / roomWidthCm,
        LEGACY_CANVAS_HEIGHT / roomHeightCm,
      );
      // Shrinking a room leaves whatever was drawn in the old, larger one
      // sitting outside the canvas, where it is clipped from view and cannot be
      // grabbed. Pull everything back inside as part of the save so what is
      // stored always matches what the plan shows.
      const tableUpdates = roomTables.map((table) => {
        const realWidth = round2(numberOr(table.real_width_cm, 80));
        const realHeight = round2(numberOr(table.real_height_cm, 80));
        const rotation = Math.round(Number(table.rotation) || 0);
        const extent = rotatedExtent(realWidth, realHeight, rotation);
        const leftCm = round2(
          clampToRoom(
            Number(table.left_cm) || 0,
            realWidth,
            roomWidthCm,
            extent.width,
          ),
        );
        const topCm = round2(
          clampToRoom(
            Number(table.top_cm) || 0,
            realHeight,
            roomHeightCm,
            extent.height,
          ),
        );

        return {
          id: table.id,
          table_name: table.table_name,
          number_of_seats: table.number_of_seats,
          round_or_square: table.round_or_square || "square",
          real_width_cm: realWidth,
          real_height_cm: realHeight,
          left_cm: leftCm,
          top_cm: topCm,
          rotation,
          isPlaced: isPlaced(table.isPlaced),
          is_locked: isPlaced(table.is_locked),
          left: Math.round(leftCm * legacyScale),
          top: Math.round(topCm * legacyScale),
          width: Math.max(24, Math.round(realWidth * legacyScale)),
          height: Math.max(24, Math.round(realHeight * legacyScale)),
        };
      });

      if (tableUpdates.length) {
        await api.patch(
          `/restaurants/${restaurants.id}/tables/bulk-update/`,
          { updates: tableUpdates },
          authConfig,
        );
      }

      if (objects.length) {
        await api.patch(
          `/restaurants/${restaurants.id}/design-items/bulk-update/`,
          {
            updates: objects.map((item) => {
              const width = round2(numberOr(item.width, 100));
              const height = round2(numberOr(item.height, 100));
              const rotation = Math.round(Number(item.rotation) || 0);
              const extent = rotatedExtent(width, height, rotation);

              return {
                id: item.id,
                items_name: item.items_name,
                item_type: item.item_type,
                left: round2(
                  clampToRoom(
                    Number(item.left) || 0,
                    width,
                    roomWidthCm,
                    extent.width,
                  ),
                ),
                top: round2(
                  clampToRoom(
                    Number(item.top) || 0,
                    height,
                    roomHeightCm,
                    extent.height,
                  ),
                ),
                width,
                height,
                rotation,
                color: item.color || COLORS.sage,
                opacity: round2(clamp(Number(item.opacity) || 1, 0.1, 1)),
                place_of_table: selectedRoom.id,
              };
            }),
          },
          authConfig,
        );
      }

      await Promise.all([
        dispatch(fetchPlaceOfTable(restaurants.id)),
        dispatch(fetchTables(restaurants.id)),
        loadObjects(),
      ]);
      setFloorPlanFile(null);
      setFloorPlanPreview("");
      setRemoveFloorPlan(false);
      setStatusMessage(t("roomDesigner.saved"));
    } catch (error) {
      setStatusError(true);
      setStatusMessage(
        apiErrorMessage(error, t("roomDesigner.errors.save")),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const createRoom = async () => {
    if (!restaurants?.id || !newRoom.name.trim()) return;
    try {
      await dispatch(
        createPlaceOfTable(restaurants.id, {
          restaurant: restaurants.id,
          place_of_table: newRoom.name.trim(),
          width: numberOr(newRoom.width, DEFAULT_ROOM_WIDTH_M),
          height: numberOr(newRoom.height, DEFAULT_ROOM_HEIGHT_M),
        }),
      );
      await dispatch(fetchPlaceOfTable(restaurants.id));
      setShowNewRoom(false);
      setNewRoom({ name: "", width: 10, height: 6 });
    } catch (error) {
      setStatusError(true);
      setStatusMessage(t("roomDesigner.errors.createRoom"));
    }
  };

  const deleteRoom = async () => {
    if (!restaurants?.id || !selectedRoom) return;
    try {
      await api.delete(
        `/restaurants/${restaurants.id}/placeoftable/${selectedRoom.id}/`,
        authConfig,
      );
      setSelectedRoomId("");
      setShowDeleteRoom(false);
      await Promise.all([
        dispatch(fetchPlaceOfTable(restaurants.id)),
        dispatch(fetchTables(restaurants.id)),
      ]);
    } catch (error) {
      setStatusError(true);
      setStatusMessage(t("roomDesigner.errors.deleteRoom"));
    }
  };

  const addTables = async () => {
    if (!restaurants?.id || !selectedRoom) return;
    try {
      await dispatch(
        createTables(restaurants.id, {
          place_of_table: selectedRoom.id,
          num_tables_to_create: numberOr(newTables.count, 1),
          num_seats: numberOr(newTables.seats, 4),
          round_or_square: newTables.shape,
          real_width_cm: numberOr(newTables.width, 80),
          real_height_cm: numberOr(newTables.height, 80),
        }),
      );
      await dispatch(fetchTables(restaurants.id));
      setShowAddTables(false);
    } catch (error) {
      setStatusError(true);
      setStatusMessage(t("roomDesigner.errors.addTables"));
    }
  };

  const roomFloorPlanUrl = floorPlanPreview ||
    (!removeFloorPlan ? resolveMediaUrl(selectedRoom?.floor_plan_image) : "");

  const objectDefinition = (type) =>
    OBJECT_LIBRARY.find((item) => item.type === type) || OBJECT_LIBRARY.at(-1);

  const renderResizeControls = (kind, entity, width, height) => {
    // An entity this small is only a handful of pixels wide or tall. Showing
    // all eight handles would leave nothing of the body exposed to grab, so
    // fall back to a single corner handle sitting just outside the box.
    const compact =
      (width / roomWidthCm) * 100 < COMPACT_HANDLE_PERCENT ||
      (height / roomHeightCm) * 100 < COMPACT_HANDLE_PERCENT;
    const handles = compact
      ? RESIZE_HANDLES.filter((handle) => handle.position === "se")
      : RESIZE_HANDLES;

    return (
      <>
        <SizeBadge>
          {round2(width)} × {round2(height)} cm ·{" "}
          {Math.round(Number(entity.rotation) || 0)}°
        </SizeBadge>
        <RotateHandle
          data-rotate-handle="true"
          aria-label={t("roomDesigner.rotateHandle")}
          onPointerDown={(event) => startRotate(event, kind, entity)}
        >
          ↻
        </RotateHandle>
        {handles.map((handle) => (
          <ResizeHandle
            key={handle.position}
            $left={handle.left}
            $top={handle.top}
            $cursor={handle.cursor}
            $compact={compact}
            data-resize-handle={handle.position}
            aria-label={t("roomDesigner.resizeHandle", {
              position: handle.position,
            })}
            onPointerDown={(event) =>
              startResize(event, kind, entity, handle.position)
            }
          />
        ))}
      </>
    );
  };


  return (
    <Page>
      <Shell>
        <Header>
          <HeaderCopy>
            <Title>{t("roomDesigner.title")}</Title>
            <Subtitle>{t("roomDesigner.subtitle")}</Subtitle>
          </HeaderCopy>

          <HeaderTools>
            <RoomPicker>
              <label htmlFor="room-layout-picker">{t("roomDesigner.room")}</label>
              <Form.Select
                id="room-layout-picker"
                value={selectedRoomId || ""}
                onChange={(event) => setSelectedRoomId(event.target.value)}
              >
                {!zones.length && <option value="">{t("roomDesigner.noRooms")}</option>}
                {zones.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.place_of_table}
                  </option>
                ))}
              </Form.Select>
            </RoomPicker>

            <ToolbarButton
              variant="outline-secondary"
              onClick={() => setShowNewRoom(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> {t("roomDesigner.newRoom")}
            </ToolbarButton>
            <ToolbarButton
              variant="outline-danger"
              disabled={!selectedRoom}
              onClick={() => setShowDeleteRoom(true)}
            >
              <FontAwesomeIcon icon={faTrash} /> {t("roomDesigner.deleteRoom")}
            </ToolbarButton>
            <SaveButton disabled={!selectedRoom || isSaving} onClick={saveLayout}>
              {isSaving ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <FontAwesomeIcon icon={faFloppyDisk} />
              )}
              {isSaving ? t("roomDesigner.saving") : t("roomDesigner.saveLayout")}
            </SaveButton>
          </HeaderTools>
        </Header>

        {selectedRoom ? (
          <>
            <Workspace>
              <StageColumn>
                <StageHeader>
                  <DimensionBadge>
                    <FontAwesomeIcon icon={faRulerCombined} />
                    {roomWidthM.toFixed(2)} m × {roomHeightM.toFixed(2)} m
                  </DimensionBadge>
                  <StageTools>
                    <Hint>{t("roomDesigner.dragHint")}</Hint>
                    <ZoomControls aria-label={t("roomDesigner.zoomControls")}>
                      <ZoomButton
                        type="button"
                        aria-label={t("roomDesigner.zoomOut")}
                        disabled={zoom <= 0.25}
                        onClick={() =>
                          setZoom((current) => clamp(current - 0.25, 0.25, 5))
                        }
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </ZoomButton>
                      <ZoomInputWrap>
                        <ZoomInput
                          type="number"
                          min="25"
                          max="500"
                          step="5"
                          value={Math.round(zoom * 100)}
                          aria-label={t("roomDesigner.zoomPercentage")}
                          onChange={(event) =>
                            setZoom(
                              clamp(Number(event.target.value) / 100, 0.25, 5),
                            )
                          }
                        />
                        <ZoomSuffix>%</ZoomSuffix>
                      </ZoomInputWrap>
                      <ZoomButton
                        type="button"
                        aria-label={t("roomDesigner.zoomIn")}
                        disabled={zoom >= 5}
                        onClick={() =>
                          setZoom((current) => clamp(current + 0.25, 0.25, 5))
                        }
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </ZoomButton>
                      <ZoomButton
                        type="button"
                        aria-label={t("roomDesigner.resetZoom")}
                        onClick={() => setZoom(1)}
                      >
                        1:1
                      </ZoomButton>
                    </ZoomControls>
                  </StageTools>
                </StageHeader>

                <CanvasViewport
                  ref={viewportRef}
                  data-testid="room-canvas-viewport"
                  tabIndex="0"
                  aria-label={t("roomDesigner.canvas")}
                  $panReady={spacePanReady}
                  $panning={isPanning}
                  onPointerDownCapture={(event) => {
                    // Capture phase: when a pan starts, the entity drag and
                    // deselect handlers underneath must never see the event.
                    if (startPan(event)) return;
                    viewportRef.current?.focus({ preventScroll: true });
                  }}
                  onAuxClick={(event) => {
                    // Suppress the middle-click autoscroll/paste behaviour.
                    if (event.button === 1) event.preventDefault();
                  }}
                >
                  <ZoomSurface $zoom={zoom} data-testid="room-zoom-surface">
                    <CanvasFrame>
                  <VerticalRuler>{roomHeightM.toFixed(2)} m</VerticalRuler>
                  <Canvas
                    ref={canvasRef}
                    $ratio={`${roomWidthM} / ${roomHeightM}`}
                    $gridX={100 / Math.max(1, roomWidthM)}
                    $gridY={100 / Math.max(1, roomHeightM)}
                    onPointerDown={() => setSelected(null)}
                  >
                    {roomFloorPlanUrl && (
                      <FloorPlan
                        src={roomFloorPlanUrl}
                        alt=""
                        $opacity={clamp(Number(roomDraft.opacity) || 0.35, 0.05, 1)}
                      />
                    )}

                    {objects.map((item) => {
                      const definition = objectDefinition(item.item_type);
                      const width = numberOr(item.width, 100);
                      const height = numberOr(item.height, 100);
                      const isSelected =
                        selected?.kind === "object" &&
                        Number(selected.id) === Number(item.id);
                      // An icon and caption only help when there is room to
                      // read them. Slabs and anything drawn small get the bare
                      // fill; the name is always in the side panel anyway.
                      const showCaption =
                        !SLAB_TYPES.has(item.item_type) &&
                        (width / roomWidthCm) * 100 >= CAPTION_MIN_PERCENT &&
                        (height / roomHeightCm) * 100 >= CAPTION_MIN_PERCENT;
                      return (
                        <EntityButton
                          type="button"
                          key={`object-${item.id}`}
                          $kind="object"
                          $left={(Number(item.left) / roomWidthCm) * 100}
                          $top={(Number(item.top) / roomHeightCm) * 100}
                          $width={(width / roomWidthCm) * 100}
                          $height={(height / roomHeightCm) * 100}
                          $rotation={Number(item.rotation) || 0}
                          $selected={isSelected}
                          data-entity-kind="object"
                          data-entity-id={item.id}
                          onPointerDown={(event) => startDrag(event, "object", item)}
                          aria-label={item.items_name}
                        >
                          <ObjectVisual
                            $type={item.item_type}
                            $color={item.color}
                            $opacity={clamp(Number(item.opacity) || 1, 0.1, 1)}
                          >
                            {item.image ? (
                              <ObjectImage src={resolveMediaUrl(item.image)} alt="" />
                            ) : (
                              showCaption && (
                                <>
                                  <span aria-hidden="true">{definition.icon}</span>
                                  <span>{item.items_name}</span>
                                </>
                              )
                            )}
                          </ObjectVisual>
                          {isSelected &&
                            renderResizeControls("object", item, width, height)}
                        </EntityButton>
                      );
                    })}

                    {placedTables.map((table) => {
                      const width = numberOr(table.real_width_cm, 80);
                      const height = numberOr(table.real_height_cm, 80);
                      const isSelected =
                        selected?.kind === "table" &&
                        Number(selected.id) === Number(table.id);
                      return (
                        <EntityButton
                          type="button"
                          key={`table-${table.id}`}
                          $kind="table"
                          $left={(Number(table.left_cm) / roomWidthCm) * 100}
                          $top={(Number(table.top_cm) / roomHeightCm) * 100}
                          $width={(width / roomWidthCm) * 100}
                          $height={(height / roomHeightCm) * 100}
                          $rotation={Number(table.rotation) || 0}
                          $selected={isSelected}
                          data-entity-kind="table"
                          data-entity-id={table.id}
                          onPointerDown={(event) => startDrag(event, "table", table)}
                          aria-label={`${t("roomDesigner.table")} ${table.table_name}`}
                        >
                          <TableVisual table={table} selected={isSelected} />
                          {isSelected &&
                            renderResizeControls("table", table, width, height)}
                        </EntityButton>
                      );
                    })}

                    {!placedTables.length && !objects.length && (
                      <CanvasEmpty>{t("roomDesigner.emptyCanvas")}</CanvasEmpty>
                    )}
                  </Canvas>
                  <HorizontalRuler>{roomWidthM.toFixed(2)} m</HorizontalRuler>
                    </CanvasFrame>
                  </ZoomSurface>
                </CanvasViewport>
              </StageColumn>

              <SidePanel>
                <PanelSection>
                  <SectionTitle>
                    <span>{t("roomDesigner.roomSettings")}</span>
                    <FontAwesomeIcon icon={faRulerCombined} />
                  </SectionTitle>
                  <Fields>
                    <UnitField>
                      <Form.Label>{t("roomDesigner.width")}</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="0.1"
                        value={roomDraft.width}
                        onChange={(event) =>
                          setRoomDraft((current) => ({
                            ...current,
                            width: event.target.value,
                          }))
                        }
                      />
                      <span>m</span>
                    </UnitField>
                    <UnitField>
                      <Form.Label>{t("roomDesigner.height")}</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="0.1"
                        value={roomDraft.height}
                        onChange={(event) =>
                          setRoomDraft((current) => ({
                            ...current,
                            height: event.target.value,
                          }))
                        }
                      />
                      <span>m</span>
                    </UnitField>

                    <div className="full">
                      <UploadBox>
                        <FontAwesomeIcon icon={faUpload} />
                        {t("roomDesigner.importPlan")}
                        <input type="file" accept="image/*" onChange={handleFloorPlan} />
                      </UploadBox>
                    </div>

                    {roomFloorPlanUrl && (
                      <div className="full">
                        <Form.Label>{t("roomDesigner.planOpacity")}</Form.Label>
                        <Form.Range
                          min="0.05"
                          max="1"
                          step="0.05"
                          value={roomDraft.opacity}
                          onChange={(event) =>
                            setRoomDraft((current) => ({
                              ...current,
                              opacity: event.target.value,
                            }))
                          }
                        />
                        <DangerLink type="button" onClick={clearFloorPlan}>
                          {t("roomDesigner.removePlan")}
                        </DangerLink>
                      </div>
                    )}
                  </Fields>
                </PanelSection>

                {roomReview && (
                  <PanelSection>
                    <SectionTitle>
                      <span>{t("roomDesigner.reviewTitle")}</span>
                      <ReviewToggle
                        type="button"
                        aria-expanded={showReviewDetail}
                        onClick={() => setShowReviewDetail((open) => !open)}
                      >
                        {showReviewDetail
                          ? t("roomDesigner.reviewLess")
                          : reviewIssueCount > 0
                            ? t("roomDesigner.reviewMore", {
                                count: reviewIssueCount,
                              })
                            : t("roomDesigner.reviewMorePlain")}
                      </ReviewToggle>
                    </SectionTitle>

                    {showReviewDetail && (
                      <>
                      <ReviewGrid>
                        <ReviewStat>
                          <b>
                            {roomReview.usableAreaM2 == null
                              ? "—"
                              : `${Math.round(roomReview.usableAreaM2)} m²`}
                          </b>
                          <span>{t("roomDesigner.reviewUsable")}</span>
                        </ReviewStat>
                        <ReviewStat
                          $warn={
                            roomReview.areaPerSeatM2 != null &&
                            roomReview.areaPerSeatM2 < TIGHT_AREA_PER_SEAT_M2
                          }
                        >
                          <b>
                            {roomReview.areaPerSeatM2 == null
                              ? "—"
                              : `${roomReview.areaPerSeatM2.toFixed(1)} m²`}
                          </b>
                          <span>
                            {t("roomDesigner.reviewPerSeat", {
                              seats: roomReview.seats,
                            })}
                          </span>
                        </ReviewStat>
                      </ReviewGrid>

                        {roomReview.usableAreaM2 != null &&
                          roomReview.usableAreaM2 / roomReview.canvasAreaM2 < 0.6 && (
                            <ReviewNote>
                              {t("roomDesigner.reviewShellSmall", {
                                percent: Math.round(
                                  (roomReview.usableAreaM2 /
                                    roomReview.canvasAreaM2) *
                                    100,
                                ),
                              })}
                            </ReviewNote>
                          )}

                        {roomReview.areaPerSeatM2 != null &&
                          roomReview.areaPerSeatM2 < TIGHT_AREA_PER_SEAT_M2 && (
                            <ReviewNote $warn>
                              {t("roomDesigner.reviewTight", {
                                value: roomReview.areaPerSeatM2.toFixed(1),
                                target: TIGHT_AREA_PER_SEAT_M2.toFixed(1),
                              })}
                            </ReviewNote>
                          )}

                        {roomReview.crowdedTables.length > 0 && (
                          <ReviewNote $warn>
                            {t("roomDesigner.reviewCrowded", {
                              aisle: SERVICE_AISLE_CM,
                              tables: roomReview.crowdedTables
                                .map((table) => table.name)
                                .join(", "),
                            })}
                          </ReviewNote>
                        )}

                        {roomReview.collidingTables.length > 0 && (
                          <ReviewNote $warn>
                            {t("roomDesigner.reviewColliding", {
                              tables: roomReview.collidingTables
                                .map((table) => table.name)
                                .join(", "),
                            })}
                          </ReviewNote>
                        )}

                        {roomReview.outsideTables.length > 0 && (
                          <ReviewNote $warn>
                            {t("roomDesigner.reviewOutside", {
                              tables: roomReview.outsideTables
                                .map((table) => table.name)
                                .join(", "),
                            })}
                          </ReviewNote>
                        )}

                        {roomReview.unreachablePlaces.length > 0 && (
                          <ReviewNote $warn>
                            {t("roomDesigner.reviewUnreachable", {
                              places: roomReview.unreachablePlaces.join(", "),
                            })}
                          </ReviewNote>
                        )}

                        {roomReview.strayElements.length > 0 && (
                          <ReviewNote $warn>
                            {t("roomDesigner.reviewStray", {
                              elements: roomReview.strayElements.join(", "),
                            })}
                          </ReviewNote>
                        )}

                        {!roomReview.crowdedTables.length &&
                          !roomReview.collidingTables.length &&
                          !roomReview.outsideTables.length &&
                          !roomReview.unreachablePlaces.length &&
                          !roomReview.strayElements.length && (
                            <ReviewNote $ok>
                              {t("roomDesigner.reviewAllClear")}
                            </ReviewNote>
                          )}
                      </>
                    )}
                  </PanelSection>
                )}

                <PanelSection>
                  <SectionTitle>
                    <span>
                      {selected?.kind === "object"
                        ? t("roomDesigner.objectProperties")
                        : t("roomDesigner.tableProperties")}
                    </span>
                    {selectedEntity && (
                      <TitleActions>
                        {selected?.kind === "object" && (
                          <ActionLink
                            type="button"
                            disabled={isAddingObject}
                            title={t("roomDesigner.duplicateHint")}
                            onClick={duplicateSelectedObject}
                          >
                            <FontAwesomeIcon icon={faClone} />{" "}
                            {t("roomDesigner.duplicate")}
                          </ActionLink>
                        )}
                        <DangerLink type="button" onClick={deleteSelectedEntity}>
                          <FontAwesomeIcon icon={faTrash} /> {t("common.delete")}
                        </DangerLink>
                      </TitleActions>
                    )}
                  </SectionTitle>

                  {!selectedEntity ? (
                    <Subtitle>{t("roomDesigner.selectHint")}</Subtitle>
                  ) : selected?.kind === "table" ? (
                    <Fields>
                      <div className="full">
                        <Form.Label>{t("roomDesigner.name")}</Form.Label>
                        <Form.Control
                          value={selectedEntity.table_name || ""}
                          onChange={(event) =>
                            updateTable(selectedEntity.id, {
                              table_name: event.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Form.Label>{t("roomDesigner.seats")}</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={selectedEntity.number_of_seats || 1}
                          onChange={(event) =>
                            updateTable(selectedEntity.id, {
                              number_of_seats: event.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Form.Label>{t("roomDesigner.shape")}</Form.Label>
                        <Form.Select
                          value={selectedEntity.round_or_square || "square"}
                          onChange={(event) =>
                            updateTable(selectedEntity.id, {
                              round_or_square: event.target.value,
                            })
                          }
                        >
                          <option value="square">{t("roomDesigner.square")}</option>
                          <option value="round">{t("roomDesigner.round")}</option>
                        </Form.Select>
                      </div>
                      <div className="full">
                        <LockRow>
                          <SwitchOnAndOff
                            id={`table-lock-${selectedEntity.id}`}
                            isOn={isPlaced(selectedEntity.is_locked)}
                            label={t("roomDesigner.lockTable")}
                            handleToggle={(event) =>
                              updateTable(selectedEntity.id, {
                                is_locked: event.target.checked,
                              })
                            }
                          />
                          <LockText htmlFor={`table-lock-${selectedEntity.id}`}>
                            <b>{t("roomDesigner.lockTable")}</b>
                            <span>{t("roomDesigner.lockTableHint")}</span>
                          </LockText>
                        </LockRow>
                      </div>
                      <UnitField>
                        <Form.Label>{t("roomDesigner.realWidth")}</Form.Label>
                        <Form.Control
                          type="number"
                          min={MIN_ENTITY_CM}
                          value={selectedEntity.real_width_cm}
                          onChange={(event) =>
                            updateTable(selectedEntity.id, {
                              real_width_cm: event.target.value,
                            })
                          }
                        />
                        <span>cm</span>
                      </UnitField>
                      <UnitField>
                        <Form.Label>{t("roomDesigner.realHeight")}</Form.Label>
                        <Form.Control
                          type="number"
                          min={MIN_ENTITY_CM}
                          value={selectedEntity.real_height_cm}
                          onChange={(event) =>
                            updateTable(selectedEntity.id, {
                              real_height_cm: event.target.value,
                            })
                          }
                        />
                        <span>cm</span>
                      </UnitField>
                      <div>
                        <Form.Label>{t("roomDesigner.rotation")}</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="359"
                          step="1"
                          value={selectedEntity.rotation || 0}
                          onChange={(event) =>
                            updateTable(selectedEntity.id, {
                              rotation: event.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="full">
                        <ToolbarButton
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => removeTableFromRoom(selectedEntity)}
                        >
                          {t("roomDesigner.returnToTray")}
                        </ToolbarButton>
                      </div>
                    </Fields>
                  ) : (
                    <Fields>
                      <div className="full">
                        <Form.Label>{t("roomDesigner.name")}</Form.Label>
                        <Form.Control
                          value={selectedEntity.items_name || ""}
                          onChange={(event) =>
                            updateObject(selectedEntity.id, {
                              items_name: event.target.value,
                            })
                          }
                        />
                      </div>
                      <UnitField>
                        <Form.Label>{t("roomDesigner.realWidth")}</Form.Label>
                        <Form.Control
                          type="number"
                          min={MIN_ENTITY_CM}
                          value={selectedEntity.width}
                          onChange={(event) =>
                            updateObject(selectedEntity.id, {
                              width: event.target.value,
                            })
                          }
                        />
                        <span>cm</span>
                      </UnitField>
                      <UnitField>
                        <Form.Label>{t("roomDesigner.realHeight")}</Form.Label>
                        <Form.Control
                          type="number"
                          min={MIN_ENTITY_CM}
                          value={selectedEntity.height}
                          onChange={(event) =>
                            updateObject(selectedEntity.id, {
                              height: event.target.value,
                            })
                          }
                        />
                        <span>cm</span>
                      </UnitField>
                      <div>
                        <Form.Label>{t("roomDesigner.color")}</Form.Label>
                        <Form.Control
                          type="color"
                          value={selectedEntity.color || COLORS.sage}
                          onChange={(event) =>
                            updateObject(selectedEntity.id, { color: event.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Form.Label>{t("roomDesigner.rotation")}</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="359"
                          step="1"
                          value={selectedEntity.rotation || 0}
                          onChange={(event) =>
                            updateObject(selectedEntity.id, {
                              rotation: event.target.value,
                            })
                          }
                        />
                      </div>
                    </Fields>
                  )}
                </PanelSection>
              </SidePanel>
            </Workspace>

            <Tray>
              <DockTabs role="tablist" aria-label={t("roomDesigner.editorTools")}>
                <DockTab
                  type="button"
                  role="tab"
                  $active={dockTab === "tables"}
                  aria-selected={dockTab === "tables"}
                  onClick={() => setDockTab("tables")}
                >
                  <FontAwesomeIcon icon={faChair} /> {t("roomDesigner.tablesTab")}
                </DockTab>
                <DockTab
                  type="button"
                  role="tab"
                  $active={dockTab === "objects"}
                  aria-selected={dockTab === "objects"}
                  onClick={() => setDockTab("objects")}
                >
                  <FontAwesomeIcon icon={faImage} /> {t("roomDesigner.objectsTab")}
                </DockTab>
              </DockTabs>

              <DockBody>
                <DockMain>
                  {dockTab === "tables" ? (
                    <>
                      <TrayHeader>
                        <TrayTitle>
                          {t("roomDesigner.availableTables")}
                          <span>{t("roomDesigner.clickToPlace")}</span>
                        </TrayTitle>
                        <ToolbarButton
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowAddTables(true)}
                        >
                          <FontAwesomeIcon icon={faPlus} />{" "}
                          {t("roomDesigner.addTables")}
                        </ToolbarButton>
                      </TrayHeader>
                      <TrayList>
                        {unplacedTables.length ? (
                          unplacedTables.map((table) => (
                            <TrayCard
                              type="button"
                              key={`tray-${table.id}`}
                              onClick={() => placeTable(table)}
                            >
                              <MiniTableWrap>
                                <TableVisual table={table} />
                              </MiniTableWrap>
                              <TrayLabel>
                                {table.table_name || table.table_number}
                              </TrayLabel>
                              <TrayMeta>
                                {table.number_of_seats}{" "}
                                {t("roomDesigner.seatsShort")} ·{" "}
                                {numberOr(table.real_width_cm, 80)}×
                                {numberOr(table.real_height_cm, 80)} cm
                              </TrayMeta>
                            </TrayCard>
                          ))
                        ) : (
                          <EmptyTray>
                            <FontAwesomeIcon icon={faChair} />{" "}
                            {t("roomDesigner.noLooseTables")}
                          </EmptyTray>
                        )}
                      </TrayList>
                    </>
                  ) : (
                    <>
                      <TrayHeader>
                        <TrayTitle>
                          {t("roomDesigner.objectsTitle")}
                          <span>{t("roomDesigner.clickObjectToAdd")}</span>
                        </TrayTitle>
                        {(isLoadingObjects || isAddingObject) && (
                          <Spinner size="sm" animation="border" />
                        )}
                      </TrayHeader>
                      <Library>
                        {OBJECT_LIBRARY.map((definition) => (
                          <LibraryButton
                            type="button"
                            key={`dock-${definition.type}`}
                            $swatch={definition.color}
                            disabled={isAddingObject}
                            onClick={() => addObject(definition)}
                          >
                            <span aria-hidden="true">{definition.icon}</span>
                            {t(`roomDesigner.objects.${definition.type}`)}
                          </LibraryButton>
                        ))}
                      </Library>
                    </>
                  )}
                </DockMain>
              </DockBody>
            </Tray>
          </>
        ) : (
          <StageColumn>
            <EmptyTray>{t("roomDesigner.createFirstRoom")}</EmptyTray>
          </StageColumn>
        )}

        {statusMessage && <Status $error={statusError}>{statusMessage}</Status>}
      </Shell>

      <Modal show={showNewRoom} onHide={() => setShowNewRoom(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("roomDesigner.newRoom")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Fields>
            <div className="full">
              <Form.Label>{t("roomDesigner.roomName")}</Form.Label>
              <Form.Control
                autoFocus
                value={newRoom.name}
                onChange={(event) =>
                  setNewRoom((current) => ({ ...current, name: event.target.value }))
                }
              />
            </div>
            <UnitField>
              <Form.Label>{t("roomDesigner.width")}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="0.1"
                value={newRoom.width}
                onChange={(event) =>
                  setNewRoom((current) => ({ ...current, width: event.target.value }))
                }
              />
              <span>m</span>
            </UnitField>
            <UnitField>
              <Form.Label>{t("roomDesigner.height")}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="0.1"
                value={newRoom.height}
                onChange={(event) =>
                  setNewRoom((current) => ({ ...current, height: event.target.value }))
                }
              />
              <span>m</span>
            </UnitField>
          </Fields>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewRoom(false)}>
            {t("common.cancel")}
          </Button>
          <OrangeButton disabled={!newRoom.name.trim()} onClick={createRoom}>
            {t("common.save")}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteRoom} onHide={() => setShowDeleteRoom(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("roomDesigner.deleteRoom")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("roomDesigner.deleteRoomConfirm", {
            room: selectedRoom?.place_of_table,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteRoom(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" onClick={deleteRoom}>
            {t("common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddTables} onHide={() => setShowAddTables(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("roomDesigner.addTables")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Fields>
            <div>
              <Form.Label>{t("roomDesigner.quantity")}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={newTables.count}
                onChange={(event) =>
                  setNewTables((current) => ({
                    ...current,
                    count: event.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Form.Label>{t("roomDesigner.seats")}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={newTables.seats}
                onChange={(event) =>
                  setNewTables((current) => ({
                    ...current,
                    seats: event.target.value,
                  }))
                }
              />
            </div>
            <div className="full">
              <Form.Label>{t("roomDesigner.shape")}</Form.Label>
              <Form.Select
                value={newTables.shape}
                onChange={(event) =>
                  setNewTables((current) => ({
                    ...current,
                    shape: event.target.value,
                  }))
                }
              >
                <option value="square">{t("roomDesigner.square")}</option>
                <option value="round">{t("roomDesigner.round")}</option>
              </Form.Select>
            </div>
            <UnitField>
              <Form.Label>{t("roomDesigner.realWidth")}</Form.Label>
              <Form.Control
                type="number"
                min={MIN_ENTITY_CM}
                value={newTables.width}
                onChange={(event) =>
                  setNewTables((current) => ({
                    ...current,
                    width: event.target.value,
                  }))
                }
              />
              <span>cm</span>
            </UnitField>
            <UnitField>
              <Form.Label>{t("roomDesigner.realHeight")}</Form.Label>
              <Form.Control
                type="number"
                min={MIN_ENTITY_CM}
                value={newTables.height}
                onChange={(event) =>
                  setNewTables((current) => ({
                    ...current,
                    height: event.target.value,
                  }))
                }
              />
              <span>cm</span>
            </UnitField>
          </Fields>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddTables(false)}>
            {t("common.cancel")}
          </Button>
          <OrangeButton onClick={addTables}>{t("common.save")}</OrangeButton>
        </Modal.Footer>
      </Modal>
    </Page>
  );
};

export default RoomLayoutEditor;
